import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, XCircle, Search, Shield, User, Building2, Calendar, FileText, Stethoscope } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ValidatePrescription() {
  const { prescriptionId } = useParams();
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get('code');
  
  const [searchCode, setSearchCode] = useState(codeFromUrl || '');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Se tem ID na URL, validar automaticamente
    if (prescriptionId) {
      validateById(prescriptionId);
    } else if (codeFromUrl) {
      validateByCode(codeFromUrl);
    }
  }, [prescriptionId, codeFromUrl]);

  const validateById = async (id) => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/validate/${id}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Receita não encontrada');
    } finally {
      setLoading(false);
    }
  };

  const validateByCode = async (code) => {
    setLoading(true);
    setError(null);
    setResult(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/validate/code/${code}`);
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Código de validação inválido');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchCode.trim()) {
      validateByCode(searchCode.trim());
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F8] py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-[#0F766E] rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-[#1F2933] mb-2">
            Validação de Receita Médica
          </h1>
          <p className="text-[#64748B]">
            Verifique a autenticidade de uma receita médica
          </p>
        </div>

        {/* Search Box */}
        {!prescriptionId && (
          <Card className="mb-6 border-[#D1D5DB]">
            <CardContent className="pt-6">
              <div className="flex gap-3">
                <Input
                  value={searchCode}
                  onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
                  placeholder="Digite o código de validação (12 caracteres)"
                  className="flex-1 h-12 text-lg font-mono tracking-wider uppercase"
                  maxLength={12}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <Button 
                  onClick={handleSearch}
                  disabled={loading || searchCode.length < 12}
                  className="h-12 px-6 bg-[#0F766E] hover:bg-[#0D6660]"
                >
                  <Search className="w-5 h-5 mr-2" />
                  Validar
                </Button>
              </div>
              <p className="text-xs text-[#64748B] mt-2">
                O código de validação está localizado no canto inferior da receita, próximo ao QR Code.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Loading */}
        {loading && (
          <Card className="border-[#D1D5DB]">
            <CardContent className="py-12 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-[#0F766E] border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-[#64748B]">Validando receita...</p>
            </CardContent>
          </Card>
        )}

        {/* Error */}
        {error && !loading && (
          <Card className="border-[#B91C1C] bg-red-50">
            <CardContent className="py-8 text-center">
              <XCircle className="w-16 h-16 text-[#B91C1C] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-[#B91C1C] mb-2">Receita Não Encontrada</h2>
              <p className="text-[#64748B]">{error}</p>
              <p className="text-sm text-[#64748B] mt-4">
                Verifique se o código foi digitado corretamente ou se a receita existe no sistema.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Success Result */}
        {result && !loading && (
          <Card className="border-[#0F766E] bg-green-50">
            <CardHeader className="border-b border-[#0F766E]/20 pb-4">
              <div className="flex items-center gap-4">
                <CheckCircle2 className="w-12 h-12 text-[#0F766E]" />
                <div>
                  <CardTitle className="text-xl text-[#0F766E]">Receita Válida e Autêntica</CardTitle>
                  <p className="text-sm text-[#64748B]">Esta receita foi verificada com sucesso</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Código de Validação */}
              <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                <p className="text-xs text-[#64748B] mb-1">Código de Validação</p>
                <p className="text-2xl font-mono font-bold text-[#0F766E] tracking-wider">
                  {result.validation_code}
                </p>
              </div>

              {/* Informações */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-[#64748B]" />
                    <p className="text-xs text-[#64748B]">Paciente</p>
                  </div>
                  <p className="font-semibold text-[#1F2933]">{result.patient_name}</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                  <div className="flex items-center gap-2 mb-2">
                    <Stethoscope className="w-4 h-4 text-[#64748B]" />
                    <p className="text-xs text-[#64748B]">Médico Responsável</p>
                  </div>
                  <p className="font-semibold text-[#1F2933]">{result.doctor_name}</p>
                  {result.doctor_crm && (
                    <p className="text-sm text-[#64748B]">CRM: {result.doctor_crm}</p>
                  )}
                </div>

                <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-4 h-4 text-[#64748B]" />
                    <p className="text-xs text-[#64748B]">Unidade de Saúde</p>
                  </div>
                  <p className="font-semibold text-[#1F2933]">{result.unit_name}</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText className="w-4 h-4 text-[#64748B]" />
                    <p className="text-xs text-[#64748B]">Medicamentos</p>
                  </div>
                  <p className="font-semibold text-[#1F2933]">{result.total_medications} medicamento(s)</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-[#64748B]" />
                    <p className="text-xs text-[#64748B]">Data de Emissão</p>
                  </div>
                  <p className="font-semibold text-[#1F2933]">{result.created_date}</p>
                </div>

                <div className="bg-white rounded-lg p-4 border border-[#D1D5DB]">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-[#64748B]" />
                    <p className="text-xs text-[#64748B]">Data de Renovação</p>
                  </div>
                  <p className="font-semibold text-[#1F2933]">{result.renewal_date}</p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="text-center pt-4">
                <Badge className="bg-[#0F766E] text-white px-4 py-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 mr-2" />
                  {result.status}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <p className="text-xs text-[#64748B]">
            Sistema de Receitas Médicas - Validação de Autenticidade
          </p>
          <p className="text-xs text-[#64748B] mt-1">
            Fundo Municipal de Saúde de São Domingos do Prata
          </p>
        </div>
      </div>
    </div>
  );
}
